import {
  all,
  call,
  getContext,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';

import { fidoV2 as fido } from '@bsd/ui-utils';

// actions
import { actions as accountActions, types } from 'modules/account';
import { actions as commonActions } from 'modules/common';

// constants
import { API_ROUTES, API_KEYS } from 'endpoints';
import { APP_PATHS } from 'routes/constants';
import { ACCOUNT_ERROR_TYPES } from 'modules/account/constants';
import {
  ANALYTICS_PAGE_ERROR,
  API_ERRORS,
  SAGA_CONTEXT_KEYS,
} from 'modules/application/constants';

// sagas
import { workers as applicationWorkers } from 'modules/application/sagas';

// selectors
import {
  getAccount,
  getSecurityQuestionDescription,
} from 'modules/account/selectors';

// utils
import { getErrorCodes, formatResponse } from 'utils/util';

const route = API_ROUTES[API_KEYS.invite];

// workers
function* getInviteWorker({ payload: { inviteToken } }) {
  const history = yield getContext(SAGA_CONTEXT_KEYS.history);
  try {
    const endpoint = route.endpoints.validate;
    const options = {
      rawResponse: true,
      data: {
        inviteToken,
      },
    };

    const rawResponse = yield call(fido.fetch, endpoint, options);
    const response = formatResponse(rawResponse);

    const { captchaSiteKey, firstName, lastName, validationToken } =
      response.body;

    if (response.status === 200) {
      yield put(
        accountActions.setInvite({
          captchaSiteKey,
          firstName,
          lastName,
          validationToken,
        })
      );
      return true;
    }
    const errorCodes = getErrorCodes(response);
    yield call(applicationWorkers.handleErrorTracking, {
      errorCodes,
      pageError: ANALYTICS_PAGE_ERROR.startPage,
    });

    switch (errorCodes[0]) {
      case API_ERRORS.inviteExpired.code:
        yield put(
          accountActions.setAccountError(ACCOUNT_ERROR_TYPES.inviteExpired)
        );
        history.push(APP_PATHS.inviteExpired);
        break;
      case API_ERRORS.userAlreadyRegistered.code:
      case API_ERRORS.accountHasPrimaryManager.code:
      case API_ERRORS.userAlreadyExistsOnAccount.code:
        yield put(
          accountActions.setAccountError(ACCOUNT_ERROR_TYPES.alreadyRegistered)
        );
        history.push(APP_PATHS.alreadyRegistered);
        break;
      default:
        history.push(APP_PATHS.enterZipCodeError);
    }
    return false;
  } catch (error) {
    yield call(applicationWorkers.handleErrorTracking, {
      pageError: ANALYTICS_PAGE_ERROR.startPage,
    });
    history.push(APP_PATHS.enterZipCodeError);
    yield put(commonActions.logError(error));
    return false;
  }
}

function* getTermsWorker() {
  try {
    const endpoint = route.endpoints.terms;
    const response = yield call(fido.fetch, endpoint);
    const { text: terms } = response.body;

    if (response.status === 200) {
      yield put(accountActions.setTerms({ terms }));
      return true;
    }
    yield call(applicationWorkers.handleErrorTracking, {});
    yield put(accountActions.setAccountError(ACCOUNT_ERROR_TYPES.terms));
    return false;
  } catch (error) {
    yield call(applicationWorkers.handleErrorTracking, {});
    yield all([
      put(commonActions.logError(error)),
      put(accountActions.setAccountError(ACCOUNT_ERROR_TYPES.terms)),
    ]);
    return false;
  }
}

function* finalizeInviteWorker({ payload }) {
  const {
    password,
    securityQuestion: securityQuestionKey,
    securityAnswer,
  } = payload;
  const history = yield getContext(SAGA_CONTEXT_KEYS.history);

  yield put(commonActions.showGlobalSpinner());

  const endpoint = API_ROUTES[API_KEYS.invite].endpoints.finalizeInvite;
  const { inviteToken, validationToken } = yield select(getAccount);
  const securityQuestionDescription = yield select(
    getSecurityQuestionDescription(securityQuestionKey)
  );

  const options = {
    data: {
      inviteToken,
      validationToken,
      password,
      securityAnswer,
      securityQuestionDescription,
      securityQuestionKey,
    },
    rawResponse: true,
  };

  try {
    const rawResponse = yield call(fido.fetch, endpoint, options);
    const response = formatResponse(rawResponse);

    if (response.status === 204) {
      yield put(accountActions.setInviteFinalized());
      history.push(APP_PATHS.confirm);
      yield put(commonActions.hideGlobalSpinner());
      return true;
    }

    const errorCodes = getErrorCodes(response);

    yield call(applicationWorkers.handleErrorTracking, {
      errorCodes,
      pageError: ANALYTICS_PAGE_ERROR.accountCreation,
    });

    switch (errorCodes[0]) {
      case API_ERRORS.inviteExpired.code:
        yield put(
          accountActions.setAccountError(ACCOUNT_ERROR_TYPES.inviteExpired)
        );
        history.push(APP_PATHS.inviteExpired);
        break;

      case API_ERRORS.userAlreadyRegistered.code:
        yield put(
          accountActions.setAccountError(ACCOUNT_ERROR_TYPES.alreadyRegistered)
        );
        history.push(APP_PATHS.alreadyRegistered);
        break;
      case API_ERRORS.passwordBanned.code:
        yield put(
          commonActions.setCommonError({
            heading: API_ERRORS.passwordBanned.heading,
            message: API_ERRORS.passwordBanned.message,
          })
        );
        break;
      case API_ERRORS.passwordInvalid.code:
        yield put(
          commonActions.setCommonError({
            heading: API_ERRORS.passwordInvalid.heading,
            message: API_ERRORS.passwordInvalid.message,
          })
        );
        break;
      default:
        yield put(
          commonActions.setCommonError({
            heading: API_ERRORS.default.heading,
            message: API_ERRORS.default.message,
          })
        );
    }
    yield put(commonActions.hideGlobalSpinner());
    return false;
  } catch (error) {
    yield call(applicationWorkers.handleErrorTracking, {
      pageError: ANALYTICS_PAGE_ERROR.accountCreation,
    });
    history.push(APP_PATHS.activateAccountError);
    yield all([
      put(commonActions.logError(error)),
      put(commonActions.hideGlobalSpinner()),
    ]);
    return false;
  }
}

/**
 * Sends the captchaToken, inviteToken and zipCode for account validation .
 *
 * @param {object} payload The user supplied form input values
 * @returns {Boolean} returns true if the account was validated
 */
function* submitVerificationWorker({ payload }) {
  try {
    const { zipCode, captchaToken } = payload;
    const history = yield getContext(SAGA_CONTEXT_KEYS.history);
    yield put(commonActions.showGlobalSpinner());

    const endpoint = route.endpoints.verifyZipCode;
    const { inviteToken, validationToken: initialValidationToken } =
      yield select(getAccount);

    const options = {
      data: {
        captchaToken,
        inviteToken,
        zipCode,
        validationToken: initialValidationToken,
      },
      rawResponse: true,
    };
    const rawResponse = yield call(fido.fetch, endpoint, options);
    const response = formatResponse(rawResponse);
    const { emailAddress, validationToken, securityQuestions } = response.body;

    if (response.status === 200) {
      yield put(
        accountActions.logVerificationSuccess({
          emailAddress,
          securityQuestions,
          validationToken,
        })
      );
      history.push(APP_PATHS.activateAccount);
      yield put(commonActions.hideGlobalSpinner());
      return true;
    }
    const errorCodes = getErrorCodes(response);
    yield call(applicationWorkers.handleErrorTracking, {
      errorCodes,
      pageError: ANALYTICS_PAGE_ERROR.zipVerify,
    });

    switch (errorCodes[0]) {
      case API_ERRORS.invalidZip.code:
        yield put(
          commonActions.setCommonError({
            heading: API_ERRORS.invalidZip.heading,
            message: API_ERRORS.invalidZip.message,
          })
        );
        break;
      case API_ERRORS.inviteExpired.code:
        yield put(
          accountActions.setAccountError(ACCOUNT_ERROR_TYPES.inviteExpired)
        );
        history.push(APP_PATHS.inviteExpired);
        break;
      case API_ERRORS.sessionTimeout.code:
        history.push(APP_PATHS.activateAccountError);
        break;
      default:
        yield put(
          commonActions.setCommonError({
            heading: API_ERRORS.default.heading,
            message: API_ERRORS.default.message,
          })
        );
    }
    yield put(commonActions.hideGlobalSpinner());
    return false;
  } catch (error) {
    yield call(applicationWorkers.handleErrorTracking, {
      pageError: ANALYTICS_PAGE_ERROR.zipVerify,
    });
    yield put(
      commonActions.setCommonError({
        heading: API_ERRORS.default.heading,
        message: API_ERRORS.default.message,
      })
    );
    yield all([
      put(commonActions.logError(error)),
      put(commonActions.hideGlobalSpinner()),
    ]);
    return false;
  }
}

export const workers = {
  getInviteWorker,
  getTermsWorker,
  finalizeInviteWorker,
  submitVerificationWorker,
};

// Watchers
function* inviteWatch() {
  yield all([
    takeLatest(types.INVITE_FINALIZE, finalizeInviteWorker),
    takeLatest(types.INVITE_GET, getInviteWorker),
    takeLatest(types.TERMS_GET, getTermsWorker),
    takeLatest(types.VERIFICATION_SUBMIT, submitVerificationWorker),
  ]);
}

export const watchers = {
  inviteWatch,
};

// Sagas
export default function* saga() {
  yield inviteWatch();
}
